import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Camera, Loader2, RotateCcw, CheckCircle2, LogOut, ArrowRight,
  Users, Trash2, Sparkles, AlertTriangle, ArrowLeft,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate, Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const KIOSK_TOKEN_KEY = 'wekeza_kiosk_token';
const KIOSK_REP_KEY = 'wekeza_kiosk_rep';

/**
 * Roster Mode — snap a photo of a multi-row NAME | CONTACT sign-in sheet,
 * GPT-4o Vision extracts every row, rep confirms/edits inline, one tap
 * to bulk-register everyone (each gets a WhatsApp magic link).
 */
export default function KioskRoster() {
  const navigate = useNavigate();
  const [kioskToken, setKioskToken] = useState(localStorage.getItem(KIOSK_TOKEN_KEY) || '');
  const repName = localStorage.getItem(KIOSK_REP_KEY) || 'Rep';
  const [phase, setPhase] = useState('idle'); // idle | scanning | review | done
  const [rows, setRows] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!kioskToken) navigate('/kiosk');
  }, [kioskToken, navigate]);

  const authHeader = { 'X-Kiosk-Token': kioskToken };

  const handleFile = async (file) => {
    if (!file) return;
    setPhase('scanning');
    setRows([]);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      setImagePreview(dataUrl);
      const { data } = await axios.post(
        `${API}/api/kiosk/scan-roster`,
        { image_base64: dataUrl },
        { headers: authHeader, timeout: 60000 },
      );
      const extracted = (data.rows || []).map((r, i) => ({
        id: `r${i}`,
        name: r.name || '',
        phone: r.phone || '',
        confidence: r.confidence || 0,
        keep: true,
      }));
      if (extracted.length === 0) {
        toast.error('No rows detected — try a clearer photo or better lighting.');
      } else {
        toast.success(`Read ${extracted.length} row${extracted.length === 1 ? '' : 's'}`);
      }
      setRows(extracted);
      setPhase('review');
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Roster scan failed';
      toast.error(String(msg));
      setPhase('idle');
    }
  };

  const reset = () => {
    setPhase('idle');
    setRows([]);
    setImagePreview(null);
    setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async () => {
    const kept = rows.filter((r) => r.keep && r.name.trim() && r.phone.trim());
    if (kept.length === 0) {
      toast.error('Nothing to register — check the rows.');
      return;
    }
    setSubmitting(true);
    try {
      const leads = kept.map((r) => ({
        name: r.name.trim(),
        phone: r.phone.trim(),
        whatsapp_phone: r.phone.trim(),
        lead_source: 'trade_fair_roster',
        send_whatsapp: true,
        send_email: false,
      }));
      const { data } = await axios.post(
        `${API}/api/kiosk/bulk-register`,
        { leads },
        { headers: authHeader, timeout: 120000 },
      );
      setResult(data);
      setPhase('done');
      toast.success(`${data.registered}/${data.total} registered`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Bulk register failed';
      toast.error(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const updateRow = (id, patch) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const logout = () => {
    localStorage.removeItem(KIOSK_TOKEN_KEY);
    setKioskToken('');
    navigate('/kiosk');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-950 via-emerald-900 to-emerald-950 text-white">
      {/* Header */}
      <header className="sticky top-0 z-10 backdrop-blur bg-black/20 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/kiosk" className="flex items-center gap-2 text-white/80 hover:text-white text-sm" data-testid="roster-back-link">
            <ArrowLeft className="w-4 h-4" /> Card mode
          </Link>
          <div className="flex items-center gap-2 text-xs text-white/60">
            <Users className="w-4 h-4 text-amber-300" />
            <span className="font-semibold text-white">Roster Mode</span>
            <span>·</span>
            <span>{repName}</span>
          </div>
          <button onClick={logout} className="text-white/60 hover:text-white text-xs flex items-center gap-1" data-testid="roster-logout">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {phase === 'idle' && (
          <div className="max-w-lg mx-auto text-center pt-8" data-testid="roster-idle">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs font-medium mb-6">
              <Sparkles className="w-3 h-3 text-amber-300" /> Bulk lead capture
            </span>
            <h1 className="text-3xl sm:text-4xl font-bold mb-3 tracking-tight">Snap the sign-in sheet</h1>
            <p className="text-white/70 mb-8">
              GPT-4o Vision reads every row of a handwritten <strong className="text-white">NAME | CONTACT</strong> roster.
              Review, tap once, and every attendee gets a WhatsApp welcome + magic link.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="roster-file-input"
            />
            <button
              onClick={() => fileRef.current?.click()}
              className="group relative w-full max-w-sm mx-auto"
              data-testid="roster-open-camera"
            >
              <div className="absolute inset-0 bg-emerald-400 rounded-2xl blur-xl opacity-40 group-hover:opacity-70 transition" />
              <div className="relative bg-emerald-500 hover:bg-emerald-400 active:scale-[0.98] rounded-2xl p-5 shadow-2xl">
                <Camera className="w-8 h-8 mx-auto mb-2" />
                <div className="text-lg font-bold">Take photo of roster</div>
                <div className="text-xs text-emerald-50/90">Works on any handwritten sheet</div>
              </div>
            </button>
          </div>
        )}

        {phase === 'scanning' && (
          <div className="text-center pt-16" data-testid="roster-scanning">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-emerald-300" />
            <p className="text-white/70">Reading the sheet…</p>
            <p className="text-xs text-white/40 mt-1">GPT-4o Vision is extracting rows</p>
          </div>
        )}

        {phase === 'review' && (
          <div data-testid="roster-review">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <CheckCircle2 className="w-6 h-6 text-emerald-300" />
                  {rows.filter((r) => r.keep).length} of {rows.length} to register
                </h2>
                <p className="text-xs text-white/50 mt-0.5">Uncheck rows to skip. Tap any field to edit.</p>
              </div>
              <button onClick={reset} className="text-white/60 hover:text-white text-xs flex items-center gap-1" data-testid="roster-rescan">
                <RotateCcw className="w-3.5 h-3.5" /> Rescan
              </button>
            </div>

            {imagePreview && (
              <details className="mb-4 bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                <summary className="px-3 py-2 text-xs text-white/60 cursor-pointer">Show original photo</summary>
                <img src={imagePreview} alt="Roster" className="w-full max-h-80 object-contain bg-black/30" />
              </details>
            )}

            <div className="bg-white/5 border border-white/10 rounded-2xl divide-y divide-white/10 overflow-hidden">
              {rows.map((r, idx) => (
                <div key={r.id} className="p-3 flex items-center gap-3" data-testid={`roster-row-${idx}`}>
                  <input
                    type="checkbox"
                    checked={r.keep}
                    onChange={(e) => updateRow(r.id, { keep: e.target.checked })}
                    className="w-5 h-5 accent-emerald-500 shrink-0"
                    data-testid={`roster-row-${idx}-keep`}
                  />
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2 min-w-0">
                    <input
                      value={r.name}
                      onChange={(e) => updateRow(r.id, { name: e.target.value })}
                      placeholder="Name"
                      className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-400 min-w-0"
                      data-testid={`roster-row-${idx}-name`}
                    />
                    <input
                      value={r.phone}
                      onChange={(e) => updateRow(r.id, { phone: e.target.value })}
                      placeholder="0754123456"
                      className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-emerald-400 min-w-0"
                      data-testid={`roster-row-${idx}-phone`}
                    />
                  </div>
                  {r.confidence < 0.7 && r.keep && (
                    <span title="Low OCR confidence — please double-check" className="text-amber-300 shrink-0">
                      <AlertTriangle className="w-4 h-4" />
                    </span>
                  )}
                  <button
                    onClick={() => updateRow(r.id, { keep: false })}
                    className="text-white/40 hover:text-rose-400 shrink-0"
                    title="Skip this row"
                    data-testid={`roster-row-${idx}-remove`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {rows.length === 0 && (
                <div className="p-6 text-center text-white/50 text-sm">No rows detected.</div>
              )}
            </div>

            <button
              onClick={submit}
              disabled={submitting || rows.filter((r) => r.keep && r.name.trim() && r.phone.trim()).length === 0}
              className="mt-6 w-full h-14 rounded-2xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-lg font-bold flex items-center justify-center gap-2 shadow-xl"
              data-testid="roster-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" /> Registering…
                </>
              ) : (
                <>
                  Register all & send WhatsApp <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        )}

        {phase === 'done' && result && (
          <div data-testid="roster-done">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center mb-3">
                <CheckCircle2 className="w-8 h-8 text-emerald-300" />
              </div>
              <h2 className="text-3xl font-bold mb-1">Done!</h2>
              <p className="text-white/70 text-sm">
                {result.registered} of {result.total} registered · WhatsApp welcomes fired
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl divide-y divide-white/10">
              {result.results.map((r, i) => (
                <div key={i} className="p-3 flex items-center gap-3 text-sm" data-testid={`roster-result-${i}`}>
                  <div className={`w-2 h-2 rounded-full shrink-0 ${r.ok ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{r.name}</div>
                    <div className="text-xs text-white/50 font-mono">{r.phone}</div>
                  </div>
                  {r.ok ? (
                    r.already_existed ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-400/30">
                        Existing
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
                        New
                      </span>
                    )
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-400/30" title={r.error}>
                      Failed
                    </span>
                  )}
                </div>
              ))}
            </div>

            <button
              onClick={reset}
              className="mt-6 w-full h-12 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-sm font-semibold flex items-center justify-center gap-2"
              data-testid="roster-scan-another"
            >
              <Camera className="w-4 h-4" /> Scan another sheet
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
